'use client';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Menu, X } from 'lucide-react';
import { isDarkColor } from './colorContrast';

/**
 * Header condiviso per landing/scroll-experience/siti marketing.
 * `overlay` lo rende fisso sopra l'hero: trasparente in cima, sticky con
 * sfondo 90% + blur non appena si scrolla oltre `scrollThreshold`.
 * `dark` forza il testo chiaro solo nella fase trasparente (hero con
 * foto/video sotto — nessun modo automatico di saperlo, va dichiarato).
 * `bgColor` personalizzato sostituisce il colore del tema nella fase
 * "solid" (scrollata): se scuro, il testo passa in chiaro in automatico.
 * In entrambi i casi il testo chiaro non è un className hardcoded su
 * logo/link: sovrascrive i token colore testo e bordo sull'intero
 * header, così anche rightSlot/cta (contenuto opaco passato da fuori, es.
 * LanguageSwitcher/ThemeToggle) eredita il contrasto giusto per cascata di
 * CSS custom properties, stesso meccanismo di SiteFooter. Layout a griglia
 * 3 colonne (non flex+space-between) così il menu centrale resta centrato
 * sull'intera larghezza anche se logo e rightSlot hanno pesi diversi.
 * Contenuto (logo, link, CTA, extra a destra) passato per props — nessun
 * contenuto hardcoded, riusabile su qualsiasi progetto.
 *
 * Sotto `sm`: layout diverso, non solo ridimensionato — hamburger a
 * sinistra (solo se `links` non è vuoto) che apre un pannello a tutto
 * schermo portalato su `document.body` (stesso motivo del pannello di
 * LanguageSwitcher: un pannello opaco a sfondo fisso non deve ereditare
 * l'override colore chiaro dell'header quando è in fase overlay/scura,
 * altrimenti testo chiaro su sfondo chiaro), logo centrato, pillole
 * (rightSlot) più piccole spostate tutte a destra. Logo e rightSlot sono
 * quindi renderizzati due volte (blocco mobile + blocco desktop, uno dei
 * due sempre `hidden`) — pattern responsive standard, evita di dover
 * rilevare il breakpoint via JS.
 *
 * `linkAs`: componente per i link di navigazione — di default un `<a href>`
 * semplice; passa `Link` di react-router-dom (o equivalente) per navigazione
 * SPA senza reload tra pagine reali. Nessun import di router qui: il
 * componente resta agnostico, decide il progetto che lo consuma.
 *
 * @param {{
 *   logo: React.ReactNode,
 *   mobileMenuLogo?: React.ReactNode,   // logo per il pannello mobile a tutto schermo — ha sempre sfondo chiaro fisso, quindi serve una variante indipendente da `dark`/stato scroll di `logo` (default: stesso `logo`)
 *   links?: Array<{ href: string, label: string, active?: boolean }>,
 *   linkAs?: React.ElementType,
 *   cta?: React.ReactNode,
 *   rightSlot?: React.ReactNode,
 *   overlay?: boolean,
 *   dark?: boolean,
 *   bgColor?: string,
 *   uppercase?: boolean,
 *   scrollThreshold?: number,
 *   className?: string,
 * }} props
 */
export function SiteHeader({
  logo, mobileMenuLogo, links = [], linkAs, cta, rightSlot,
  overlay = false, dark = false, bgColor, uppercase = false, scrollThreshold = 16, className = '',
}) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (!overlay) return;
    const onScroll = () => setScrolled(window.scrollY > scrollThreshold);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [overlay, scrollThreshold]);

  useEffect(() => {
    if (!mobileOpen) return undefined;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const solid = !overlay || scrolled;
  const customDark = solid && bgColor && isDarkColor(bgColor);
  const lightText = (dark && !solid) || customDark;
  const LinkTag = linkAs || 'a';

  return (
    <header
      className={[
        // Sopra 1000: i controlli di Leaflet (blocco Mappa) usano
        // z-index:1000 di loro — con l'header a un valore più basso (z-20,
        // pensato solo per stare sopra il contenuto normale della pagina),
        // una mappa "widescreen" scrollata sotto l'header ci finiva sopra.
        'inset-x-0 top-0 z-[1050] px-6 transition-all duration-300',
        overlay ? 'fixed' : 'relative',
        solid ? 'py-3 border-b border-theme-border' : 'py-5 border-b border-transparent',
        className,
      ].join(' ')}
      style={{
        ...(solid ? {
          backgroundColor: bgColor || 'color-mix(in srgb, var(--color-bg) 90%, transparent)',
          backdropFilter: 'blur(10px)',
        } : {}),
        ...(lightText ? {
          '--color-text': '#FFFFFF',
          '--color-text-muted': 'rgba(255,255,255,.72)',
          '--color-text-subtle': 'rgba(255,255,255,.48)',
          '--color-border': 'rgba(255,255,255,.25)',
        } : {}),
      }}
    >
      <div className="mx-auto max-w-6xl">
        {/* Mobile: hamburger sx (solo se c'è un menu) — logo centrato — pillole dx, più piccole */}
        <div className="flex items-center justify-between gap-4 sm:hidden">
          <div className="flex w-9 shrink-0 items-center justify-start">
            {links.length > 0 && (
              <button
                onClick={() => setMobileOpen(true)}
                aria-label="Apri menu"
                aria-expanded={mobileOpen}
                className="inline-flex h-8 w-8 items-center justify-center rounded-[12px] border border-theme-border text-theme-text-muted transition-colors hover:border-theme-text-subtle hover:bg-theme-bg-subtle hover:text-theme-text"
              >
                <Menu size={16} />
              </button>
            )}
          </div>
          <div className="text-theme-text">{logo}</div>
          <div className="flex w-9 shrink-0 items-center justify-end gap-2 [&_button]:h-7 [&_button]:w-7">
            {rightSlot}
          </div>
        </div>

        {/* Desktop: griglia a 3 colonne invariata */}
        <div className="hidden items-center gap-6 sm:grid sm:grid-cols-[1fr_auto_1fr]">
          <div className="justify-self-start text-theme-text">{logo}</div>

          <nav aria-label="Menu principale" className="flex items-center justify-self-center gap-1">
            {links.map((link) => (
              <LinkTag
                key={link.href}
                {...(linkAs ? { to: link.href } : { href: link.href })}
                className={[
                  'rounded-[5px] px-3 py-1.5 text-sm transition-colors',
                  uppercase ? 'uppercase tracking-wide text-xs' : '',
                  link.active ? 'text-theme-accent' : 'text-theme-text-muted hover:text-theme-text',
                ].join(' ')}
                style={link.active ? { backgroundColor: lightText ? 'rgba(255,255,255,0.15)' : 'color-mix(in srgb, var(--color-accent) 10%, transparent)' } : undefined}
              >
                {link.label}
              </LinkTag>
            ))}
          </nav>

          <div className="flex items-center justify-self-end gap-3">
            {rightSlot}
            {cta}
          </div>
        </div>
      </div>

      {links.length > 0 && createPortal(
        <div
          className={[
            'fixed inset-0 z-[1150] flex flex-col bg-theme-bg px-6 py-5 transition-transform duration-300 sm:hidden',
            mobileOpen ? 'translate-x-0' : '-translate-x-full pointer-events-none',
          ].join(' ')}
          role="dialog"
          aria-modal="true"
          aria-label="Menu"
        >
          <div className="flex items-center justify-between">
            <div className="text-theme-text">{mobileMenuLogo || logo}</div>
            <button
              onClick={() => setMobileOpen(false)}
              aria-label="Chiudi menu"
              className="inline-flex h-8 w-8 items-center justify-center rounded-[12px] border border-theme-border text-theme-text-muted transition-colors hover:border-theme-text-subtle hover:bg-theme-bg-subtle hover:text-theme-text"
            >
              <X size={16} />
            </button>
          </div>
          <nav aria-label="Menu principale" className="mt-4 flex flex-1 flex-col items-center justify-center gap-2">
            {links.map((link) => (
              <LinkTag
                key={link.href}
                {...(linkAs ? { to: link.href } : { href: link.href })}
                onClick={() => setMobileOpen(false)}
                className={[
                  'rounded-lg px-4 py-3 text-lg transition-colors',
                  uppercase ? 'uppercase tracking-wide' : '',
                  link.active ? 'text-theme-accent' : 'text-theme-text',
                ].join(' ')}
              >
                {link.label}
              </LinkTag>
            ))}
          </nav>
        </div>,
        document.body
      )}
    </header>
  );
}
